import React from 'react';
import { Box, Popover } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import cx from 'classnames';
import { LearnMoreIcon } from '@Icons-outdated';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

const useStyles = makeStyles(theme => ({
  popoverWrapper: {
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.13)'
  },
  popover: {
    width: '356px',
    border: '1px solid #F0F2F6',
    borderRadius: '4px'
  },
  popoverTitle: {
    paddingLeft: theme.spacing(2.25),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2.5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #e2e2e2'
  },
  titleText: {
    fontSize: '14px',
    lineHeight: '21px',
    fontWeight: 500
  },
  popoverContent: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(2.5),
    paddingLeft: theme.spacing(2.25),
    paddingRight: theme.spacing(2.75),
    fontSize: '12px',
    lineHeight: '18px'
  },
  learnMore: {
    color: '#5155F5',
    fontSize: 14,
    '&:hover': {
      textDecoration: 'underline'
    },
    '&:visited': {
      color: '#5155F5'
    },
    '& svg': {
      marginLeft: '6px',
      marginBottom: '-1px'
    }
  },
  throttlingReasonsWrapper: {
    display: 'flex',
    marginTop: '12px',
    flexDirection: 'column',
    flexWrap: 'wrap'
  },
  reasonItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px'
  },
  reasonIndicator: {
    borderRadius: '50%',
    width: '8px',
    height: '8px',
    backgroundColor: '#12A4B4',
    flexShrink: 0
  },
  metrics: {
    backgroundColor: '#FB9341'
  },
  params: {
    backgroundColor: '#ED4A7B'
  },
  stdout: {
    backgroundColor: '#945FCF'
  },
  graph: {
    backgroundColor: '#5899DA'
  },
  system_metrics: {
    backgroundColor: '#19A979'
  }
}));

const ThrottlingPopover = ({ row, anchorEl, onClose }) => {
  const classes = useStyles();

  // Avoid showing True and False as throttling reasons - BE issue
  const validateReason = reason => {
    return reason !== 'True' && reason !== 'False';
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        horizontal: 'right',
        vertical: 'bottom'
      }}
      elevation={0}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}
      classes={{
        paper: classes.popoverWrapper
      }}
    >
      <div className={classes.popover}>
        <div className={classes.popoverTitle}>
          <div className={classes.titleText}>Throttled experiment</div>
          <Box
            component="a"
            className={classes.learnMore}
            href="/docs/v2/guides/experiment-management/limits-and-performance/#rate-limits"
            target="_blank"
          >
            Learn more
            <LearnMoreIcon />
          </Box>
        </div>
        <div className={classes.popoverContent}>
          <div>Rate limit reached!</div>
          <Box mt={1.5}>
            The experiment has been throttled due to logging too many data too
            quickly. Mission-critical data, like Artifacts, are never throttled.
          </Box>
          {!isEmpty(row?.throttlingReasons) && (
            <div className={classes.throttlingReasonsWrapper}>
              {row.throttlingReasons.map(
                (reason, index) =>
                  validateReason(reason) && (
                    <div className={classes.reasonItem} key={index}>
                      <div
                        className={cx(
                          classes.reasonIndicator,
                          classes[reason] || {}
                        )}
                      />
                      {reason}
                    </div>
                  )
              )}
            </div>
          )}
        </div>
      </div>
    </Popover>
  );
};

ThrottlingPopover.propTypes = {
  row: PropTypes.object.isRequired,
  anchorEl: PropTypes.any.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ThrottlingPopover;
